import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../context/CartContext";
import PageLoader from "../components/PageLoader";
import "../styles/style_panier.css";

// Données de démonstration pour le panier
const DEMO_CART_ITEMS = [
  {
    itemId: "1-M",
    product: {
      id: 1,
      uuid: "demo-1",
      libelle: "Bouquet Romance",
      description: "Un magnifique bouquet de roses rouges et roses, symbole d'amour et de passion",
      categorie: "Bouquets",
      imgPrincipal: null,
      tva: 20.00,
      variantes: [
        { taille_code: "M", taille_libelle: "Moyen", prixHT: 35.00, stock: 10 },
        { taille_code: "L", taille_libelle: "Grand", prixHT: 50.00, stock: 8 },
        { taille_code: "XL", taille_libelle: "Très Grand", prixHT: 75.00, stock: 5 }
      ]
    },
    variant: { taille_code: "M", taille_libelle: "Moyen", prixHT: 35.00, stock: 10 },
    quantity: 2,
    bouquetType: null,
    selected: true,
    addedAt: new Date().toISOString()
  },
  {
    itemId: "99-L-bulle",
    product: {
      id: 99,
      uuid: "demo-99",
      libelle: "Bouquet Personnalisable",
      description: "Choisissez la taille et le type de bouquet (tige ou bulle) selon vos envies.",
      categorie: "Bouquets",
      imgPrincipal: null,
      tva: 20.00,
      variantes: [
        { taille_code: "S", taille_libelle: "Petit", prixHT: 25.00, stock: 12 },
        { taille_code: "M", taille_libelle: "Moyen", prixHT: 40.00, stock: 7 },
        { taille_code: "L", taille_libelle: "Grand", prixHT: 60.00, stock: 3 }
      ],
      bouquetTypeOptions: ["tige", "bulle"],
      bouquetTypeDefault: "bulle"
    },
    variant: { taille_code: "L", taille_libelle: "Grand", prixHT: 60.00, stock: 3 },
    quantity: 1,
    bouquetType: "bulle",
    selected: true,
    addedAt: new Date().toISOString()
  },
  {
    itemId: "3-M",
    product: {
      id: 3,
      uuid: "demo-3",
      libelle: "Orchidée Élégance",
      description: "Une orchidée Phalaenopsis en pot décoratif, pour une touche d'élégance",
      categorie: "Plantes",
      imgPrincipal: null,
      tva: 20.00,
      variantes: [
        { taille_code: "M", taille_libelle: "2 tiges", prixHT: 45.00, stock: 7 },
        { taille_code: "L", taille_libelle: "3 tiges", prixHT: 65.00, stock: 4 }
      ]
    },
    variant: { taille_code: "M", taille_libelle: "2 tiges", prixHT: 45.00, stock: 7 },
    quantity: 1,
    bouquetType: null,
    selected: false,
    addedAt: new Date().toISOString()
  },
  {
    itemId: "7-L",
    product: {
      id: 7,
      uuid: "demo-7",
      libelle: "Bouquet Mariage",
      description: "Le bouquet parfait pour le plus beau jour de votre vie",
      categorie: "Mariage",
      imgPrincipal: null,
      tva: 20.00,
      variantes: [
        { taille_code: "M", taille_libelle: "Classique", prixHT: 90.00, stock: 4 },
        { taille_code: "L", taille_libelle: "Premium", prixHT: 140.00, stock: 3 },
        { taille_code: "XL", taille_libelle: "Luxe", prixHT: 200.00, stock: 2 }
      ]
    },
    variant: { taille_code: "L", taille_libelle: "Premium", prixHT: 140.00, stock: 3 },
    quantity: 1,
    bouquetType: null,
    selected: true,
    addedAt: new Date().toISOString()
  },
  {
    itemId: "5-M",
    product: {
      id: 5,
      uuid: "demo-5",
      libelle: "Rose Éternelle",
      description: "Une rose stabilisée sous cloche, conserve sa beauté pendant des années",
      categorie: "Cadeaux",
      imgPrincipal: null,
      tva: 20.00,
      variantes: [
        { taille_code: "M", taille_libelle: "Standard", prixHT: 55.00, stock: 2 }
      ]
    },
    variant: { taille_code: "M", taille_libelle: "Standard", prixHT: 55.00, stock: 2 },
    quantity: 2,
    bouquetType: null,
    selected: true,
    addedAt: new Date().toISOString()
  }
];

// Composant Modal pour modifier la configuration d'un article
function EditItemModal({ item, isOpen, onClose, onSave, formatPrice, calcTTC }) {
  const [selectedVariant, setSelectedVariant] = useState(item?.variant || null);
  const [selectedBouquetType, setSelectedBouquetType] = useState(item?.bouquetType || null);

  if (!isOpen || !item) return null;

  const product = item.product;
  const hasMultipleVariants = product.variantes && product.variantes.length > 1;
  const hasBouquetTypeOptions = Array.isArray(product.bouquetTypeOptions) && product.bouquetTypeOptions.length > 0;

  const handleSave = () => {
    onSave(item.itemId, selectedVariant, selectedBouquetType);
    onClose();
  };

  const currentPrice = selectedVariant ? calcTTC(selectedVariant.prixHT, product.tva) : 0;

  return (
    <div className="edit-modal-overlay" onClick={onClose}>
      <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
        <button className="edit-modal-close" onClick={onClose}>
          <ion-icon name="close-outline"></ion-icon>
        </button>

        <div className="edit-modal-header">
          <ion-icon name="settings-outline"></ion-icon>
          <h2>Modifier la configuration</h2>
        </div>

        <div className="edit-modal-product">
          <h3>{product.libelle}</h3>
          <span className="edit-modal-category">{product.categorie}</span>
        </div>

        <div className="edit-modal-content">
          {/* Sélection de la taille */}
          {hasMultipleVariants && (
            <div className="edit-section">
              <label className="edit-section-label">
                <ion-icon name="resize-outline"></ion-icon>
                Choisir la taille
              </label>
              <div className="edit-size-options">
                {product.variantes.map((variant) => {
                  const isSelected = selectedVariant?.taille_code === variant.taille_code;
                  const isOutOfStock = variant.stock === 0;
                  const priceTTC = calcTTC(variant.prixHT, product.tva);

                  return (
                    <button
                      key={variant.taille_code}
                      className={`edit-size-btn ${isSelected ? "selected" : ""} ${isOutOfStock ? "disabled" : ""}`}
                      onClick={() => !isOutOfStock && setSelectedVariant(variant)}
                      disabled={isOutOfStock}
                    >
                      <span className="size-name">{variant.taille_libelle}</span>
                      <span className="size-price">{formatPrice(priceTTC)} €</span>
                      {isOutOfStock && <span className="size-out">Rupture</span>}
                      {!isOutOfStock && variant.stock <= 3 && (
                        <span className="size-low">Plus que {variant.stock}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sélection du type de bouquet */}
          {hasBouquetTypeOptions && (
            <div className="edit-section">
              <label className="edit-section-label">
                <ion-icon name="leaf-outline"></ion-icon>
                Type de bouquet
              </label>
              <div className="edit-type-options">
                {product.bouquetTypeOptions.map((type) => (
                  <button
                    key={type}
                    className={`edit-type-btn ${selectedBouquetType === type ? "selected" : ""}`}
                    onClick={() => setSelectedBouquetType(type)}
                  >
                    <span className="type-icon">
                      {type === "tige" ? "🌷" : "🌸"}
                    </span>
                    <span className="type-name">{type === "tige" ? "En tige" : "En bulle"}</span>
                    <span className="type-desc">
                      {type === "tige" ? "Bouquet classique" : "Composition ronde"}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Affichage si aucune option */}
          {!hasMultipleVariants && !hasBouquetTypeOptions && (
            <div className="edit-no-options">
              <ion-icon name="information-circle-outline"></ion-icon>
              <p>Ce produit n'a pas d'options configurables.</p>
            </div>
          )}
        </div>

        {/* Prix et boutons */}
        <div className="edit-modal-footer">
          <div className="edit-price-preview">
            <span className="price-label">Nouveau prix unitaire</span>
            <span className="price-value">{formatPrice(currentPrice)} €</span>
          </div>
          <div className="edit-modal-actions">
            <button className="btn-cancel" onClick={onClose}>
              Annuler
            </button>
            <button className="btn-save" onClick={handleSave}>
              <ion-icon name="checkmark-outline"></ion-icon>
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Panier() {
  const navigate = useNavigate();
  
  // Mode démo : true pour voir le mockup, false pour utiliser le vrai panier
  const [demoMode] = useState(true);
  
  const {
    cartItems: realCartItems,
    removeFromCart,
    updateQuantity,
    updateItemConfig,
    toggleSelection,
    selectAll,
    removeSelected,
    clearCart,
    totals: realTotals,
    calculateTTC,
  } = useCart();

  // État local pour le mode démo
  const [demoItems, setDemoItems] = useState(DEMO_CART_ITEMS);

  const [loading, setLoading] = useState(false);
  
  // État pour le modal d'édition
  const [editingItem, setEditingItem] = useState(null);

  // Utiliser les données démo ou réelles selon le mode
  const cartItems = demoMode ? demoItems : realCartItems;

  // Calculer les totaux pour le mode démo
  const demoTotals = useMemo(() => {
    const selectedItems = demoItems.filter((item) => item.selected);
    const allItems = demoItems;

    const calcTTC = (prixHT, tva) => prixHT * (1 + tva / 100);

    const calculateSubtotal = (items) =>
      items.reduce((sum, item) => {
        const priceTTC = calcTTC(item.variant.prixHT, item.product.tva);
        return sum + priceTTC * item.quantity;
      }, 0);

    return {
      itemCount: allItems.length,
      selectedCount: selectedItems.length,
      subtotalAll: calculateSubtotal(allItems),
      subtotalSelected: calculateSubtotal(selectedItems),
      totalQuantity: allItems.reduce((sum, item) => sum + item.quantity, 0),
      selectedQuantity: selectedItems.reduce((sum, item) => sum + item.quantity, 0),
    };
  }, [demoItems]);

  const totals = demoMode ? demoTotals : realTotals;

  // Fonctions pour le mode démo
  const handleToggleSelection = (itemId) => {
    if (demoMode) {
      setDemoItems(prev => prev.map(item => 
        item.itemId === itemId ? { ...item, selected: !item.selected } : item
      ));
    } else {
      toggleSelection(itemId);
    }
  };

  const handleSelectAll = (selected) => {
    if (demoMode) {
      setDemoItems(prev => prev.map(item => ({ ...item, selected })));
    } else {
      selectAll(selected);
    }
  };

  const handleUpdateQuantity = (itemId, quantity) => {
    if (demoMode) {
      setDemoItems(prev => prev.map(item => 
        item.itemId === itemId 
          ? { ...item, quantity: Math.max(1, Math.min(quantity, item.variant.stock)) } 
          : item
      ));
    } else {
      updateQuantity(itemId, quantity);
    }
  };

  const handleRemoveFromCart = (itemId) => {
    if (demoMode) {
      setDemoItems(prev => prev.filter(item => item.itemId !== itemId));
    } else {
      removeFromCart(itemId);
    }
  };

  const handleRemoveSelected = () => {
    if (demoMode) {
      setDemoItems(prev => prev.filter(item => !item.selected));
    } else {
      removeSelected();
    }
  };

  const handleClearCart = () => {
    if (demoMode) {
      setDemoItems([]);
    } else {
      clearCart();
    }
  };

  // Mettre à jour la configuration d'un article
  const handleUpdateItemConfig = (itemId, newVariant, newBouquetType) => {
    if (demoMode) {
      setDemoItems(prev => {
        const itemIndex = prev.findIndex(item => item.itemId === itemId);
        if (itemIndex === -1) return prev;

        const item = prev[itemIndex];
        const product = item.product;
        
        // Créer le nouvel identifiant
        const newItemId = `${product.id}-${newVariant.taille_code}${newBouquetType ? `-${newBouquetType}` : ""}`;
        
        // Vérifier si un item avec cette config existe déjà
        const existingIndex = prev.findIndex(i => i.itemId === newItemId && i.itemId !== itemId);
        
        if (existingIndex > -1) {
          // Fusionner avec l'item existant
          const updated = [...prev];
          const newQuantity = Math.min(
            updated[existingIndex].quantity + item.quantity,
            newVariant.stock
          );
          updated[existingIndex] = {
            ...updated[existingIndex],
            quantity: newQuantity,
          };
          return updated.filter((_, index) => index !== itemIndex);
        }
        
        // Mettre à jour l'item avec la nouvelle config
        const updated = [...prev];
        updated[itemIndex] = {
          ...item,
          itemId: newItemId,
          variant: newVariant,
          bouquetType: newBouquetType,
          quantity: Math.min(item.quantity, newVariant.stock),
        };
        return updated;
      });
    } else {
      updateItemConfig(itemId, newVariant, newBouquetType);
    }
  };

  // Vérifier si un article est configurable
  const isItemConfigurable = (item) => {
    const hasMultipleVariants = item.product.variantes && item.product.variantes.length > 1;
    const hasBouquetOptions = Array.isArray(item.product.bouquetTypeOptions) && item.product.bouquetTypeOptions.length > 0;
    return hasMultipleVariants || hasBouquetOptions;
  };

  const calcTTC = (prixHT, tva) => prixHT * (1 + tva / 100);

  // Vérifier si tous les items sont sélectionnés
  const allSelected = useMemo(() => {
    return cartItems.length > 0 && cartItems.every((item) => item.selected);
  }, [cartItems]);

  // Vérifier si certains items sont sélectionnés
  const someSelected = useMemo(() => {
    return cartItems.some((item) => item.selected);
  }, [cartItems]);

  // Formater le prix
  const formatPrice = (price) => {
    return price.toFixed(2).replace(".", ",");
  };

  // Obtenir l'emoji de la fleur basé sur l'ID du produit
  const getFlowerEmoji = (productId) => {
    const flowerEmojis = ["🌸", "🌺", "🌷", "🌹", "💐", "🌻", "🪻", "🪷"];
    return flowerEmojis[productId % flowerEmojis.length];
  };

  // Image placeholder avec gradient
  const getPlaceholderStyle = (productId) => ({
    background: `linear-gradient(135deg, 
      hsl(${(productId * 30) % 360}, 70%, 85%), 
      hsl(${(productId * 30 + 40) % 360}, 60%, 75%))`,
  });

  if (loading) return <PageLoader label="Chargement du panier" />;

  return (
    <div className="panier-page">
      {/* Bandeau mode démo */}
      {demoMode && (
        <div className="demo-banner">
          <ion-icon name="information-circle-outline"></ion-icon>
          <span>Mode démonstration - Ce panier contient des articles fictifs pour prévisualisation</span>
        </div>
      )}

      {/* Header décoratif */}
      <div className="panier-header">
        <div className="panier-header-overlay"></div>
        <div className="panier-header-content">
          <div className="floral-decoration left">
            <ion-icon name="flower-outline"></ion-icon>
          </div>
          <h1 className="panier-title">Mon Panier</h1>
          <p className="panier-subtitle">
            {cartItems.length === 0
              ? "Votre panier est vide"
              : `${totals.totalQuantity} article${totals.totalQuantity > 1 ? "s" : ""} dans votre panier`}
          </p>
          <div className="floral-decoration right">
            <ion-icon name="flower-outline"></ion-icon>
          </div>
        </div>
        <div className="floating-petals">
          <span className="petal petal-1">🌸</span>
          <span className="petal petal-2">🌺</span>
          <span className="petal petal-3">🌷</span>
          <span className="petal petal-4">💐</span>
          <span className="petal petal-5">🌹</span>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="panier-content">
        {cartItems.length === 0 ? (
          /* Panier vide */
          <div className="panier-empty">
            <div className="empty-illustration">
              <ion-icon name="cart-outline"></ion-icon>
              <span className="empty-flower">🌸</span>
            </div>
            <h2>Votre panier est vide</h2>
            <p>
              Découvrez nos magnifiques créations florales et ajoutez vos coups
              de cœur à votre panier.
            </p>
            <Link to="/boutique" className="btn-continue-shopping">
              <ion-icon name="flower-outline"></ion-icon>
              <span>Découvrir la boutique</span>
            </Link>
          </div>
        ) : (
          /* Panier avec articles */
          <div className="panier-container">
            {/* Liste des articles */}
            <div className="panier-items-section">
              {/* Barre d'actions */}
              <div className="panier-actions-bar">
                <label className="select-all-checkbox">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">
                    Tout sélectionner ({cartItems.length})
                  </span>
                </label>

                <div className="action-buttons">
                  {someSelected && (
                    <button
                      className="btn-action delete"
                      onClick={handleRemoveSelected}
                    >
                      <ion-icon name="trash-outline"></ion-icon>
                      <span>Supprimer la sélection</span>
                    </button>
                  )}
                  <button
                    className="btn-action clear"
                    onClick={() => {
                      if (window.confirm("Voulez-vous vraiment vider votre panier ?")) {
                        handleClearCart();
                      }
                    }}
                  >
                    <ion-icon name="close-circle-outline"></ion-icon>
                    <span>Vider le panier</span>
                  </button>
                </div>
              </div>

              {/* Liste des articles */}
              <div className="panier-items-list">
                {cartItems.map((item) => {
                  const priceTTC = calcTTC(
                    item.variant.prixHT,
                    item.product.tva
                  );
                  const totalItem = priceTTC * item.quantity;

                  return (
                    <div
                      key={item.itemId}
                      className={`panier-item ${item.selected ? "selected" : ""}`}
                    >
                      {/* Checkbox de sélection */}
                      <label className="item-checkbox">
                        <input
                          type="checkbox"
                          checked={item.selected}
                          onChange={() => handleToggleSelection(item.itemId)}
                        />
                        <span className="checkmark"></span>
                      </label>

                      {/* Image du produit */}
                      <div className="item-image">
                        {item.product.imgPrincipal ? (
                          <img
                            src={item.product.imgPrincipal}
                            alt={item.product.libelle}
                          />
                        ) : (
                          <div
                            className="item-image-placeholder"
                            style={getPlaceholderStyle(item.product.id)}
                          >
                            {getFlowerEmoji(item.product.id)}
                          </div>
                        )}
                      </div>

                      {/* Informations produit */}
                      <div className="item-details">
                        <div className="item-header">
                          <h3 className="item-title">{item.product.libelle}</h3>
                          <span className="item-category">
                            {item.product.categorie}
                          </span>
                        </div>

                        <div className="item-variants">
                          <span className="variant-tag">
                            <ion-icon name="resize-outline"></ion-icon>
                            {item.variant.taille_libelle}
                          </span>
                          {item.bouquetType && (
                            <span className="variant-tag type">
                              <ion-icon name="leaf-outline"></ion-icon>
                              {item.bouquetType === "tige" ? "Tige" : "Bulle"}
                            </span>
                          )}
                        </div>

                        <p className="item-description">
                          {item.product.description}
                        </p>

                        {/* Prix unitaire */}
                        <div className="item-unit-price">
                          <span className="price-label">Prix unitaire :</span>
                          <span className="price-value">
                            {formatPrice(priceTTC)} €
                          </span>
                        </div>
                      </div>

                      {/* Contrôles quantité et prix */}
                      <div className="item-controls">
                        {/* Sélecteur de quantité */}
                        <div className="quantity-selector">
                          <button
                            className="qty-btn minus"
                            onClick={() =>
                              handleUpdateQuantity(item.itemId, item.quantity - 1)
                            }
                            disabled={item.quantity <= 1}
                          >
                            <ion-icon name="remove-outline"></ion-icon>
                          </button>
                          <input
                            type="number"
                            className="qty-input"
                            value={item.quantity}
                            onChange={(e) => {
                              const val = parseInt(e.target.value) || 1;
                              handleUpdateQuantity(item.itemId, val);
                            }}
                            min="1"
                            max={item.variant.stock}
                          />
                          <button
                            className="qty-btn plus"
                            onClick={() =>
                              handleUpdateQuantity(item.itemId, item.quantity + 1)
                            }
                            disabled={item.quantity >= item.variant.stock}
                          >
                            <ion-icon name="add-outline"></ion-icon>
                          </button>
                        </div>

                        {/* Stock restant */}
                        {item.variant.stock <= 5 && (
                          <span className="stock-warning">
                            <ion-icon name="alert-circle-outline"></ion-icon>
                            Plus que {item.variant.stock} en stock
                          </span>
                        )}

                        {/* Total ligne */}
                        <div className="item-total">
                          <span className="total-label">Total</span>
                          <span className="total-value">
                            {formatPrice(totalItem)} €
                          </span>
                        </div>

                        {/* Boutons d'action */}
                        <div className="item-action-buttons">
                          {/* Bouton modifier */}
                          {isItemConfigurable(item) && (
                            <button
                              className="btn-edit-item"
                              onClick={() => setEditingItem(item)}
                              title="Modifier la configuration"
                            >
                              <ion-icon name="settings-outline"></ion-icon>
                            </button>
                          )}

                          {/* Bouton supprimer */}
                          <button
                            className="btn-remove-item"
                            onClick={() => handleRemoveFromCart(item.itemId)}
                            title="Retirer du panier"
                          >
                            <ion-icon name="trash-outline"></ion-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bouton continuer les achats */}
              <div className="continue-shopping">
                <Link to="/boutique" className="btn-continue">
                  <ion-icon name="arrow-back-outline"></ion-icon>
                  <span>Continuer mes achats</span>
                </Link>
              </div>
            </div>

            {/* Résumé de commande */}
            <div className="panier-summary">
              <div className="summary-card">
                <h2 className="summary-title">
                  <ion-icon name="receipt-outline"></ion-icon>
                  Récapitulatif
                </h2>

                {/* Détails du résumé */}
                <div className="summary-details">
                  <div className="summary-row">
                    <span>Articles sélectionnés</span>
                    <span>
                      {totals.selectedCount} / {totals.itemCount}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span>Quantité totale</span>
                    <span>{totals.selectedQuantity} article{totals.selectedQuantity > 1 ? "s" : ""}</span>
                  </div>
                  <div className="summary-divider"></div>
                  <div className="summary-row subtotal">
                    <span>Sous-total</span>
                    <span>{formatPrice(totals.subtotalSelected)} €</span>
                  </div>
                  <div className="summary-row shipping">
                    <span>Livraison</span>
                    <span className="shipping-info">
                      {totals.subtotalSelected >= 50
                        ? "Offerte"
                        : "Calculée à l'étape suivante"}
                    </span>
                  </div>
                  {totals.subtotalSelected < 50 && totals.subtotalSelected > 0 && (
                    <div className="free-shipping-notice">
                      <ion-icon name="gift-outline"></ion-icon>
                      <span>
                        Plus que{" "}
                        <strong>{formatPrice(50 - totals.subtotalSelected)} €</strong>{" "}
                        pour la livraison offerte !
                      </span>
                    </div>
                  )}
                </div>

                {/* Total */}
                <div className="summary-total">
                  <span>Total TTC</span>
                  <span className="total-price">
                    {formatPrice(totals.subtotalSelected)} €
                  </span>
                </div>

                {/* Bouton commander */}
                <button
                  className="btn-checkout"
                  disabled={totals.selectedCount === 0}
                  onClick={() => navigate("/checkout")}
                >
                  <ion-icon name="card-outline"></ion-icon>
                  <span>Procéder au paiement</span>
                </button>

                {/* Moyens de paiement */}
                <div className="payment-methods">
                  <span className="payment-label">Paiement sécurisé</span>
                  <div className="payment-icons">
                    <ion-icon name="card-outline"></ion-icon>
                    <ion-icon name="logo-paypal"></ion-icon>
                    <ion-icon name="shield-checkmark-outline"></ion-icon>
                  </div>
                </div>

                {/* Garanties */}
                <div className="summary-guarantees">
                  <div className="guarantee-item">
                    <ion-icon name="leaf-outline"></ion-icon>
                    <span>Fleurs fraîches garanties</span>
                  </div>
                  <div className="guarantee-item">
                    <ion-icon name="time-outline"></ion-icon>
                    <span>Livraison le jour même</span>
                  </div>
                  <div className="guarantee-item">
                    <ion-icon name="heart-outline"></ion-icon>
                    <span>Satisfaction garantie</span>
                  </div>
                </div>
              </div>

              {/* Code promo */}
              <div className="promo-code-card">
                <h3 className="promo-title">
                  <ion-icon name="pricetag-outline"></ion-icon>
                  Code promo
                </h3>
                <div className="promo-input-group">
                  <input
                    type="text"
                    placeholder="Entrez votre code"
                    className="promo-input"
                  />
                  <button className="btn-apply-promo">Appliquer</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section info */}
      <div className="panier-info-section">
        <div className="info-card">
          <ion-icon name="shield-checkmark-outline"></ion-icon>
          <h3>Paiement Sécurisé</h3>
          <p>Vos données sont protégées par un cryptage SSL</p>
        </div>
        <div className="info-card">
          <ion-icon name="refresh-outline"></ion-icon>
          <h3>Satisfait ou Remboursé</h3>
          <p>Échange ou remboursement sous 14 jours</p>
        </div>
        <div className="info-card">
          <ion-icon name="chatbubbles-outline"></ion-icon>
          <h3>Service Client</h3>
          <p>Notre équipe est à votre écoute 7j/7</p>
        </div>
      </div>

      {/* Modal de modification */}
      <EditItemModal
        item={editingItem}
        isOpen={!!editingItem}
        onClose={() => setEditingItem(null)}
        onSave={handleUpdateItemConfig}
        formatPrice={formatPrice}
        calcTTC={calcTTC}
      />
    </div>
  );
}
